<script setup lang="ts">
  import useTaboolaAds, {
    getAdBrandFromTile,
    claimPlacementIndex,
    matchesPlacement,
    placementName,
    subscribeToTaboola,
  } from '../../composables/ad-ops/useTaboolaAds'
  import { useNativeAd } from '../../composables/ad-ops/useNativeAd'

  /**
   * Props for the Taboola component
   * @property containerName - The name of the container for the ad
   * @property placement - The placement identifier for the ad
   * @property mode - style of the ad
   * @property tilePosition - Optional position of the tile in a list
   * @property adHeight - Optional height of the ad in pixels
   * @property adWidth - Optional width of the ad in pixels
   * @property  lazyRender - controls -visibilityThresholdInPercentage - Optional percentage of the element that must be visible
   * AND visibilityTimeoutInMs - Optional timeout in milliseconds before rendering when visible
   * @property adInstanceId - Optional unique identifier for the ad instance
   * @property sticky - Optional sticky class for the ad
   */
  export interface TaboolaProps {
    containerName: string
    placement: string
    mode: string
    tilePosition?: number
    isFeed?: boolean
    adInstanceId?: string
    lazyRender?: boolean
    sticky?: boolean
    adHeight?: string
    adWidth?: string
    ayInstanceCode?: string
    adRefresh?: boolean
  }

  const props = withDefaults(defineProps<TaboolaProps>(), {
    tilePosition: -1,
    isFeed: false,
    adInstanceId: '',
    lazyRender: false,
    sticky: false,
    adHeight: 'auto',
    adWidth: 'auto',
    ayInstanceCode: '',
  })

  const widgetWrapperEl = useTemplateRef<HTMLElement | null>('widgetWrapperEl')

  const { renderWidget } = useTaboolaAds()

  const containerName = ref<string>(props.containerName)
  const widgetName = ref<string>(props.placement)
  const placementClassName = ref<string>(props.placement)

  const { $assertive } = useNuxtApp()
  const taboolaExternId = $assertive?.analytics?.custom?.custom_4 || ''

  let brandAtRequest = ''

  const eventData = (brand: string): NtpAdEvent => ({
    ntp_ad_brand: brand,
    ntp_tile_position: props.tilePosition,
    ntp_ad_provider: 'taboola',
    ntp_creative_id: 'taboola-' + props.placement,
    ntp_placement_name: props.placement,
    ntp_extern_id: taboolaExternId,
    ...(props.ayInstanceCode && {
      ntp_ay_instance_code: props.ayInstanceCode,
      ntp_ad_refresh: props.adRefresh,
    }),
  })

  const { showWidget, adBrand } = useNativeAd({
    provider: 'taboola',
    props,
    wrapper: widgetWrapperEl,
    subscribe: (handlers) =>
      subscribeToTaboola(
        (placement) => matchesPlacement(placement, placementClassName.value),
        () => widgetWrapperEl.value,
        handlers
      ),
    clickSlotId: props.placement,
    eventData,
    tileBrand: getAdBrandFromTile,
    renderBrand: () => brandAtRequest,
    nameInstance: (pageIndex) => {
      const index = widgetWrapperEl.value
        ? claimPlacementIndex(props.placement, pageIndex, widgetWrapperEl.value)
        : pageIndex
      if (index > 0) {
        // for taboola properties endless starts at 4 since other units take <4
        const taboolaStartIndex = 4
        widgetName.value = widgetName.value + (index + taboolaStartIndex)
        containerName.value = props.containerName + (index + taboolaStartIndex)
        // class name we want the instance of the ad (not the same as taboola)
        placementClassName.value = placementName(props.placement, index)
      }
    },
    request: () => {
      brandAtRequest = getAdBrandFromTile(widgetWrapperEl.value)
      return renderWidget(
        props.mode,
        containerName.value,
        placementClassName.value
      )
    },
  })
</script>
<template>
  <div
    ref="widgetWrapperEl"
    :key="props.adInstanceId"
    :class="[props.placement, placementClassName, widgetName]"
    :data-ntp-extern-id="taboolaExternId"
    :data-ad-instance-id="props.adInstanceId"
  >
    <div
      v-if="showWidget"
      :id="containerName"
      class="TABOOLA"
      :data-ad-brand="adBrand"
    />
  </div>
</template>
